import {useCallback, useEffect, useMemo, useState} from 'react';
import {ApiError, errorMessage} from './api';
import {useAuth} from './auth';
import {Empty, Notice, Spinner} from './components';
import {AuditEventCard} from './components/AuditEventCard';
import {Icon} from './components/Icon';
import type {AuditEvent} from './types';
import {auditKindLabel, auditKindsPresent, cachedIdentities, filterAuditEvents} from './utils';

export function AuditPage() {
  const {api, gate, dataEpoch, refreshAccess} = useAuth();
  const [items, setItems] = useState<AuditEvent[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [kind, setKind] = useState('all');
  const [text, setText] = useState('');

  const load = useCallback(async (cursor?: string, append = false) => {
    if (!api) return;
    const snap = gate.snapshot(); setLoading(true); setError('');
    try {
      const page = await api.audit(cursor, snap.signal);
      if (!gate.accepts(snap.generation)) return;
      setItems(previous => append ? [...previous, ...page.items] : page.items);
      setNextCursor(page.nextCursor);
    } catch (caught) {
      if (!gate.accepts(snap.generation)) return;
      setError(errorMessage(caught));
      if (caught instanceof ApiError && [401, 403].includes(caught.status)) void refreshAccess();
    } finally { if (gate.accepts(snap.generation)) setLoading(false); }
  }, [api, gate, refreshAccess]);

  useEffect(() => { void load(); }, [dataEpoch, load]);

  const kinds = useMemo(() => auditKindsPresent(items), [items]);
  const visible = useMemo(() => filterAuditEvents(items, {kind, text}), [items, kind, text]);
  // Les identités déjà résolues par la vue Utilisateurs servent ici sans nouvel appel réseau.
  const identities = useMemo(() => cachedIdentities(), [items]);
  const filtering = kind !== 'all' || !!text.trim();

  return <section className="panel">
    <div className="panel-head"><h2>Historique des accès</h2><span className="count">Non modifiable · acteur, cible, motif, avant/après</span></div>
    <div className="filters">
      <label className="field"><Icon name="search" size={14} />
        <input type="search" value={text} onChange={event => setText(event.target.value)} placeholder="Acteur, cible, motif ou identifiant" aria-label="Filtrer les événements chargés" />
      </label>
      <div className="chips">
        <button type="button" className={kind === 'all' ? 'chip f on' : 'chip f'} aria-pressed={kind === 'all'} onClick={() => setKind('all')}>Tous</button>
        {kinds.map(item => <button key={item} type="button" className={kind === item ? 'chip f on' : 'chip f'} aria-pressed={kind === item} onClick={() => setKind(item)}>{auditKindLabel(item)}</button>)}
      </div>
    </div>
    <p className="help">Les filtres portent sur les événements chargés : le serveur ne pagine que par identifiant.</p>
    {error && <Notice tone="danger">{error}</Notice>}
    {loading && !items.length ? <Spinner />
      : visible.length ? <div className="x-events">{visible.map((item, index) => <AuditEventCard key={item.id} event={item} identities={identities} open={index === 0} />)}</div>
      : items.length ? <Empty>Aucun événement chargé ne correspond à ce filtre.</Empty>
      : <Empty>Aucun événement dans cette page.</Empty>}
    <div className="list-foot">
      {nextCursor && <button type="button" className="btn" disabled={loading} onClick={() => void load(nextCursor, true)}>Charger la page suivante</button>}
      <p className="order-note">Ordre par identifiant, pas par date · {filtering ? `${visible.length} sur ${items.length} événements chargés` : `${items.length} événement${items.length > 1 ? 's' : ''} sur cette page`}</p>
    </div>
  </section>;
}
